<!-- MAIA - Analyze & Report: Build your overview. Driven by the backend-computed report definition. -->
<div class="maia-report-step" data-testid="maia-report-step">


    <header class="step-header">
        <div class="step-heading">
            <h1>Build your overview</h1>
            <p>
                MAPTYCS selected the strongest supported sections from your enriched file. Review the overview
                below, reorder or remove sections, then generate the workbook.
            </p>
            <div class="step-meta">Step 5 · Analyze &amp; Report</div>
        </div>
    </header>

    <div class="report-body">
        <div class="report-title-field">
            <label for="reportTitle">Report title</label>
            <input id="reportTitle" type="text" [(ngModel)]="reportTitle" (ngModelChange)="onTitleInput()"
                   [class.invalid]="titleError" placeholder="Enter a report title" />
            <p class="report-error" *ngIf="titleError">Enter a report title before generating the overview.</p>
            <p class="report-note" *ngIf="!titleError">Create and export multiple overviews from this enriched file.</p>
        </div>

        <p class="overview-loading" *ngIf="loading">Building your overview…</p>
        <p class="report-error" *ngIf="error && !loading">Something went wrong building the overview. Please try again.</p>

        <!-- File overview KPI panel -->
        <div class="overview-panel" *ngIf="!loading && stats.length">
            <div class="overview-primary">
                <span>{{ primaryLabel }}</span>
                <strong>{{ primaryValue }}</strong>
                <small>{{ primarySub }}</small>
            </div>
            <div class="overview-detail">
                <div class="overview-stat" *ngFor="let s of stats">
                    <span>{{ s.label }}</span>
                    <strong>{{ s.value }}</strong>
                    <small *ngIf="s.sub">{{ s.sub }}</small>
                </div>
            </div>
        </div>

        <!-- Report value (primary measure) -->
        <div class="report-value" *ngIf="definition">
            <div class="rv-head">
                <span class="rv-label">Report value:
                    <strong>{{ definition.primaryMeasure || 'Locations' }}</strong>
                    <span class="rv-auto">Automatically selected</span>
                </span>
                <button type="button" class="link-button" (click)="showMeasureEditor = !showMeasureEditor">
                    {{ showMeasureEditor ? 'Cancel' : 'Change' }}
                </button>
                <span class="rv-computing" *ngIf="computing">Recalculating report…</span>
            </div>
            <p class="rv-note">Used for portfolio exposure breakouts throughout the workbook.</p>
            <select *ngIf="showMeasureEditor" class="rv-select"
                    (change)="selectMeasure($any($event.target).value)">
                <option [value]="''" [selected]="!definition.primaryMeasure">Locations (count)</option>
                <option *ngFor="let f of measureFields" [value]="f.name" [selected]="f.name === definition.primaryMeasure">
                    {{ f.name }}<ng-container *ngIf="f.populated != null"> ({{ f.populated }}% populated)</ng-container>
                </option>
            </select>
        </div>

        <div class="actions-row end">
            <button type="button" class="btn btn-secondary" (click)="downloadEnriched()">Download Enriched File</button>
            <button type="button" class="btn btn-primary" [disabled]="exporting || loading"
                    (click)="generateOverview()">
                {{ exporting ? 'Generating…' : 'Generate Overview' }}
            </button>
        </div>

        <div class="report-sections-head" *ngIf="definition">
            <div class="rsh-text">
                <h2>Report sections</h2>
                <p class="rsh-count">{{ definition.sections.length }}
                    workbook section{{ definition.sections.length === 1 ? '' : 's' }}. Add a breakout under an existing
                    report section or create a new section, then drag to refine the worksheet order.</p>
            </div>
            <button type="button" class="btn btn-accent" (click)="showAddContent = true">+ Add report content</button>
        </div>

        <!-- Report sections (drag to reorder; tables from the computed report) -->
        <div class="sections-list" cdkDropList (cdkDropListDropped)="dropSection($event)" *ngIf="definition">
        <div class="report-section" *ngFor="let section of definition.sections" cdkDrag
             [class.breakout]="section.parentSectionId" [class.collapsed]="isCollapsed(section.id)"
             [hidden]="section.parentSectionId && isCollapsed(section.parentSectionId)">
            <div class="rs-head">
                <button type="button" class="drag-handle" cdkDragHandle title="Drag to reorder"
                        aria-label="Drag to reorder">⠿</button>
                <button type="button" class="collapse-btn" (click)="toggleCollapse(section.id)"
                        [attr.aria-expanded]="!isCollapsed(section.id)" title="Expand or collapse">
                    {{ isCollapsed(section.id) ? '▸' : '▾' }}
                </button>
                <div class="rs-title">
                    <h3>{{ section.title }}</h3>
                    <p class="rs-subtitle">
                        Excel: {{ section.title }}<ng-container *ngIf="definition?.primaryMeasure"> · {{ definition?.primaryMeasure }}</ng-container><ng-container *ngIf="section.scope"> · {{ section.scope }}</ng-container>
                    </p>
                </div>
                <div class="rs-controls">
                    <button type="button" class="rs-btn" *ngIf="!isBreakout(section)"
                            (click)="openBreakout(section.id)">+ Breakout</button>
                    <button type="button" class="rs-btn move" *ngIf="isBreakout(section)"
                            (click)="editBreakout(section)">Move / edit</button>
                    <button type="button" class="rs-btn remove" (click)="removeSection(section.id)">Remove</button>
                </div>
            </div>

            <ng-container *ngIf="!isCollapsed(section.id)">
            <!-- Breakout: inline regroup control (no sub-tables; it groups the whole portfolio). -->
            <div class="rs-breakout-config" *ngIf="isBreakout(section)">
                <p class="rs-guidance">Choose how to group the portfolio.
                    {{ definition?.primaryMeasure || 'Locations' }} is summed automatically, with portfolio share and
                    location context added in Excel.</p>
                <label class="rs-group-field">
                    <span>Group portfolio by</span>
                    <select [ngModel]="breakoutDimension(section)"
                            (ngModelChange)="updateBreakoutDimension(section, $event)">
                        <option *ngFor="let f of fileFields" [value]="f.name">{{ f.name }}</option>
                    </select>
                </label>
            </div>

            <!-- Section subsection management (multi-part sections only, not breakouts). -->
            <div class="rs-subs" *ngIf="!isBreakout(section) && !isFootprint(section) && section.subsections.length > 1">
                <div class="rs-sub-row" *ngFor="let sub of section.subsections" [class.excluded]="!sub.included">
                    <span>{{ sub.title }}</span>
                    <button type="button" class="link-button" [class.danger]="sub.included"
                            (click)="toggleSubsection(section.id, sub.key)">
                        {{ sub.included ? 'Remove' : '+ Add' }}
                    </button>
                </div>
            </div>

            <!-- Geographical Footprint: one row per table - drag to reorder, expand, remove / re-add. -->
            <div class="footprint-rows" *ngIf="isFootprint(section)" cdkDropList
                 (cdkDropListDropped)="dropSubsection(section, $event)">
                <div class="subsection-panel" *ngFor="let sub of section.subsections" cdkDrag
                     [class.excluded]="!sub.included">
                    <div class="subsection-head">
                        <button type="button" class="drag-handle" cdkDragHandle title="Drag to reorder"
                                aria-label="Drag to reorder">⠿</button>
                        <button type="button" class="subsection-summary" [disabled]="!sub.included"
                                (click)="toggleSubsectionOpen(section.id, sub.key)"
                                [attr.aria-expanded]="isSubsectionOpen(section.id, sub.key)">
                            <span class="subsection-chevron">{{ sub.included && isSubsectionOpen(section.id, sub.key) ? '▾' : '▸' }}</span>
                            <span class="subsection-title">{{ subsectionTitle(section, sub) }}</span>
                        </button>
                        <button type="button" class="link-button" [class.danger]="sub.included"
                                (click)="toggleSubsection(section.id, sub.key)">
                            {{ sub.included ? 'Remove' : '+ Add' }}
                        </button>
                    </div>
                    <ng-container *ngIf="sub.included && isSubsectionOpen(section.id, sub.key)">
                        <div class="subsection-body" *ngIf="subsectionTable(section.id, sub.key) as t">
                            <div class="table-scroll">
                                <table class="report-table">
                                    <thead>
                                        <tr>
                                            <th *ngFor="let col of t.columns" [class.num]="isNumericType(col.type)">
                                                {{ col.label }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let row of t.rows">
                                            <td *ngFor="let cell of row; let c = index"
                                                [class.num]="isNumericType(t.columns[c]?.type)">
                                                {{ formatCell(cell, t.columns[c]?.type) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </ng-container>
                </div>
            </div>

            <ng-container *ngIf="!isFootprint(section) && computedSection(section.id) as cs">
                <!-- Category sections: collapsible subsection panels (prototype excel-panel behaviour) -->
                <ng-container *ngIf="isCollapsibleSection(cs); else plainTables">
                    <div class="insight-strip" *ngIf="usesCoverage(cs)">
                        <strong>Coverage</strong><span>{{ coverageStrip(cs) }}</span>
                    </div>
                    <div class="subsection-panel" [class.open]="isTableOpen(section.id, i)"
                         *ngFor="let t of cs.tables; let i = index">
                        <button type="button" class="subsection-summary" (click)="toggleTable(section.id, i)"
                                [attr.aria-expanded]="isTableOpen(section.id, i)">
                            <span class="subsection-chevron">{{ isTableOpen(section.id, i) ? '▾' : '▸' }}</span>
                            <span class="subsection-title">{{ t.title }}<ng-container
                                *ngIf="usesCoverage(cs) && t.coverage != null"> · {{ coveragePct(t) }}
                                financial coverage</ng-container></span>
                        </button>
                        <div class="subsection-body" *ngIf="isTableOpen(section.id, i)">
                            <p class="subsection-copy" *ngIf="usesCoverage(cs)">This is the category table written to
                                Excel.</p>
                            <div class="table-scroll">
                                <table class="report-table">
                                    <thead>
                                        <tr>
                                            <th *ngFor="let col of t.columns" [class.num]="isNumericType(col.type)">
                                                {{ col.label }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let row of t.rows">
                                            <td *ngFor="let cell of row; let c = index"
                                                [class.num]="isNumericType(t.columns[c]?.type)">
                                                {{ formatCell(cell, t.columns[c]?.type) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </ng-container>

                <ng-template #plainTables>
                    <div class="insight-strip" *ngIf="atAGlance(cs) as glance">
                        <strong>At a glance</strong><span>{{ glance }}</span>
                    </div>
                    <div class="rs-table" [class.card]="t.title" *ngFor="let t of cs.tables">
                        <div class="rst-head" *ngIf="t.title">{{ t.title }}</div>
                        <div class="table-scroll">
                            <table class="report-table">
                                <thead>
                                    <tr>
                                        <th *ngFor="let col of t.columns" [class.num]="isNumericType(col.type)">
                                            {{ col.label }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let row of t.rows">
                                        <td *ngFor="let cell of row; let c = index"
                                            [class.num]="isNumericType(t.columns[c]?.type)">
                                            <a *ngIf="isLinkType(t.columns[c]?.type) && cell" class="map-link"
                                               [href]="cell" target="_blank" rel="noopener">Open in Google Maps</a>
                                            <ng-container *ngIf="!isLinkType(t.columns[c]?.type)">
                                                {{ formatCell(cell, t.columns[c]?.type) }}
                                            </ng-container>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </ng-template>
            </ng-container>
            </ng-container>
        </div>
        </div>

        <!-- Peril data glossary (read-only reference, from the computed report) -->
        <div class="sections-list" *ngIf="glossary.length">
            <div class="report-section glossary-section" [class.collapsed]="isCollapsed(glossaryId)">
                <div class="rs-head">
                    <span class="drag-handle disabled" aria-hidden="true">⠿</span>
                    <button type="button" class="collapse-btn" (click)="toggleCollapse(glossaryId)"
                            [attr.aria-expanded]="!isCollapsed(glossaryId)" title="Expand or collapse">
                        {{ isCollapsed(glossaryId) ? '▸' : '▾' }}
                    </button>
                    <div class="rs-title">
                        <h3>Peril data glossary</h3>
                        <p class="rs-subtitle">{{ glossaryCount }}
                            subsection{{ glossaryCount === 1 ? '' : 's' }}</p>
                    </div>
                </div>

                <ng-container *ngIf="!isCollapsed(glossaryId)">
                    <div class="glossary-group" *ngFor="let g of glossary">
                        <div class="glossary-provider">{{ g.provider }}</div>
                        <div class="rs-table card" *ngFor="let c of g.categories">
                            <div class="rst-head">{{ g.provider }} · {{ c.title }}</div>
                            <div class="table-scroll">
                                <table class="report-table">
                                    <thead>
                                        <tr>
                                            <th>Score / Category</th>
                                            <th>Definition</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let row of c.rows">
                                            <td>{{ row[0] }}</td>
                                            <td>{{ row[1] }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </ng-container>
            </div>
        </div>

        <div class="add-content-row" *ngIf="definition">
            <!-- .add-content-row is space-between, so a lone button sits left.
                 This spacer takes the "first child" slot and pushes it right. -->
            <span class="actions-spacer"></span>
            <button type="button" class="btn btn-accent" (click)="showAddContent = true">+ Add report content</button>
        </div>

        <!-- Bottom action bar (mirrors the top so the actions aren't missed after scrolling) -->
        <div class="actions-row bottom" *ngIf="definition">
            <span class="actions-spacer"></span>
            <button type="button" class="btn btn-secondary" (click)="downloadEnriched()">Download Enriched File</button>
            <button type="button" class="btn btn-primary" [disabled]="exporting || loading"
                    (click)="generateOverview()">
                {{ exporting ? 'Generating…' : 'Generate Overview' }}
            </button>
        </div>
    </div>

    <footer class="step-footer">
        <button type="button" class="btn btn-secondary" (click)="backRequested.emit()">← Back</button>
        <button type="button" class="btn btn-primary" (click)="continueRequested.emit()">Import to Maptycs →</button>
    </footer>

    <!-- Add report content modal -->
    <div class="maia-modal-overlay" *ngIf="showAddContent" (click)="showAddContent = false">
        <div class="maia-modal picker-card" (click)="$event.stopPropagation()">
            <div class="picker-head">
                <div>
                    <h3>Add report content</h3>
                    <p class="modal-sub">Only sections supported by this file are shown.</p>
                </div>
                <button type="button" class="modal-close" aria-label="Close"
                        (click)="showAddContent = false">×</button>
            </div>

            <button type="button" class="picker-custom" (click)="openBreakout(null)">
                <span class="pc-text">
                    <strong>Add a custom breakout</strong>
                    <small>Choose what to group by and where it belongs before anything is added.</small>
                </span>
                <b class="pc-config">Configure&nbsp; →</b>
            </button>

            <div class="picker-divider">Suggested for this portfolio</div>

            <div class="picker-list" *ngIf="removedSections.length; else noSuggested">
                <button type="button" class="picker-row" *ngFor="let s of removedSections"
                        (click)="readdSection(s.id)">
                    <span class="pr-text">
                        <strong>{{ s.title }}</strong>
                        <span class="pr-scope" *ngIf="s.scope">{{ s.scope }}</span>
                    </span>
                    <span class="picker-action">+ Add</span>
                </button>
            </div>
            <ng-template #noSuggested>
                <p class="picker-empty">All supported sections are already in your report. Build a custom breakout above.</p>
            </ng-template>
        </div>
    </div>

    <!-- Custom breakout modal -->
    <div class="maia-modal-overlay" *ngIf="showBreakout" (click)="showBreakout = false">
        <div class="maia-modal breakout-card" (click)="$event.stopPropagation()">
            <div class="picker-head">
                <div>
                    <h3>{{ editingBreakoutId ? 'Edit custom breakout' : 'Add a custom breakout' }}</h3>
                    <p class="modal-sub">Each breakout becomes its own Excel worksheet.</p>
                </div>
                <button type="button" class="modal-close" aria-label="Close"
                        (click)="showBreakout = false">×</button>
            </div>

            <p class="modal-sub bk-help">The selected Report value is summed automatically. Choose the portfolio
                field and where it belongs in the report.</p>

            <label class="bk-field">
                <span>Group portfolio by</span>
                <select [(ngModel)]="breakoutField">
                    <option [value]="''" disabled>Choose a field…</option>
                    <option *ngFor="let f of fileFields" [value]="f.name">
                        {{ f.name }}<ng-container *ngIf="f.populated != null"> ({{ f.populated }}% populated)</ng-container>
                    </option>
                </select>
            </label>

            <label class="bk-field">
                <span>Place under</span>
                <select [(ngModel)]="breakoutParentId">
                    <option *ngFor="let s of placementSections" [ngValue]="s.id">Under {{ s.title }}</option>
                </select>
            </label>

            <div class="bk-foot">
                <button type="button" class="btn btn-secondary" (click)="showBreakout = false">Cancel</button>
                <button type="button" class="btn btn-primary" [disabled]="!breakoutField"
                        (click)="addBreakout()">{{ editingBreakoutId ? 'Save breakout' : 'Add breakout' }}</button>
            </div>
        </div>
    </div>
</div>
